import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownloadOutlined } from '@ant-design/icons';
import { Button, Image, Spin, Tooltip } from 'antd';
import { useTranslate } from '@/translations';
import { useConversationImageSources } from '../hooks/useConversationImageSources';

export interface ConversationImage {
  eventId: string;
  mxc: string;
  alt: string;
}

export interface ConversationImageGalleryProps {
  images: readonly ConversationImage[];
  selectedEventId: string | null;
  onClose(): void;
}

const placeholder =
  'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export const ConversationImageGallery = ({
  images,
  selectedEventId,
  onClose,
}: ConversationImageGalleryProps) => {
  const { t } = useTranslate();
  const [navigation, setNavigation] = useState<{
    openedFrom: string | null;
    currentEventId: string | null;
  }>({ openedFrom: null, currentEventId: null });
  const activeEventId =
    navigation.openedFrom === selectedEventId
      ? navigation.currentEventId
      : selectedEventId;
  const currentIndex = images.findIndex(
    (image) => image.eventId === activeEventId,
  );
  const visible = selectedEventId !== null && currentIndex >= 0;
  const sources = useConversationImageSources(
    images,
    visible ? currentIndex : null,
  );

  useEffect(() => {
    if (selectedEventId === null) {
      setNavigation({ openedFrom: null, currentEventId: null });
      return;
    }
    if (currentIndex < 0) {
      setNavigation({ openedFrom: null, currentEventId: null });
      onClose();
    }
  }, [currentIndex, onClose, selectedEventId]);

  const close = () => {
    setNavigation({ openedFrom: null, currentEventId: null });
    onClose();
  };

  return (
    <Image.PreviewGroup
      items={images.map((image) => ({
        src: sources.get(image.eventId)?.url ?? placeholder,
        alt: image.alt,
      }))}
      preview={{
        visible,
        current: Math.max(0, currentIndex),
        destroyOnClose: true,
        rootClassName: 'command-centre-chat__gallery',
        countRender: (current, total) => `${current} / ${total}`,
        onChange: (nextIndex) => {
          const next = images[nextIndex];
          if (!next) return;
          setNavigation({
            openedFrom: selectedEventId,
            currentEventId: next.eventId,
          });
        },
        onVisibleChange: (nextVisible) => {
          if (!nextVisible) close();
        },
        imageRender: (originalNode, info) => {
          const image = images[info.current];
          const source = image ? sources.get(image.eventId) : undefined;
          if (source?.failed)
            return (
              <div
                role="alert"
                className="command-centre-chat__gallery-media-state"
              >
                <span>{t('commsMediaUnavailable')}</span>
                <Button
                  type="primary"
                  onClick={source.retry}
                  aria-label={t('commsRetryMedia')}
                >
                  {t('commsRetryMedia')}
                </Button>
              </div>
            );
          if (!source?.url || source.loading)
            return (
              <div
                role="status"
                className="command-centre-chat__gallery-media-state"
              >
                <Spin />
                <span>{t('commsMediaLoading')}</span>
              </div>
            );
          return originalNode;
        },
        toolbarRender: (originalNode, info) => {
          const image = images[info.current];
          const source = image ? sources.get(image.eventId) : undefined;
          return (
            <>
              {originalNode}
              {image &&
                source?.url &&
                !source.failed &&
                typeof document !== 'undefined' &&
                createPortal(
                  <Tooltip title={t('commsDownloadImage')}>
                    <a
                      className="command-centre-chat__gallery-download"
                      href={source.url}
                      download={image.alt}
                      aria-label={t('commsDownloadImage')}
                    >
                      <DownloadOutlined />
                    </a>
                  </Tooltip>,
                  document.body,
                )}
            </>
          );
        },
      }}
    />
  );
};
